import { endpoints, host } from "@config/endpoints";
import { getRefreshToken, getToken, isJwtTokenExpired, logout } from "@helpers/utils";
import axios from "axios";

interface TokenModel {
  token?: string;
  refreshToken?: string;
}

const axiosInstance = axios.create();

let isRefreshing = false;
let refreshSubscribers: any[] = [];

const onRrefreshed = (data: TokenModel) => {
  refreshSubscribers.sort((a, b) => a.index - b.index);

  refreshSubscribers.forEach(async ({ callback, index }) => {
    if (typeof callback === "function") {
      await callback(data);
    }
  });

  refreshSubscribers = [];
};

const addRefreshSubscriber = (index: number, callback: any) => {
  refreshSubscribers.push({ index, callback });
};

axiosInstance.interceptors.request.use(
  (config) => {
    const accessToken = getToken();
    if (config && config.headers && accessToken) {
      config.headers["Authorization"] = "Bearer " + accessToken;
    }
    return config;
  },
  (error) => {
    Promise.reject(error);
  }
);

axiosInstance.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
      const { path } = endpoints.users.refreshToken;
      const { config } = error;
      let status;
      if (axios.isCancel(error)) {
          status = "Call is cancelled";
          error.message = "Call is cancelled";
      }
      else {
          status = error.response.status;
      }
    
    const originalRequest = config;

    // To validate error response for a user who is trying to access api using invalid token.
    if (
      status === 403 &&
      error.response.data === "Invalid Session: Token expired"
    ) {
      logout();
    }

    // To validate if Access token is expired and request for new access token.
    if (status === 401 && !originalRequest._retry && isJwtTokenExpired()) {
      if (!isRefreshing) {
        isRefreshing = true;
        const accessToken = getToken();
        const refreshToken = getRefreshToken();

        return new Promise((resolve, reject) => {
          axios
            .post(`${host}${path}`, {
              token: accessToken,
              refreshToken: refreshToken,
            })
            .then(({ data }) => {
              localStorage.setItem("labour-navigator-jwt-token", data.token);
              localStorage.setItem(
                "labour-navigator-refresh-token",
                data.refreshToken
              );
              axiosInstance.defaults.headers.common["Authorization"] =
                "Bearer " + accessToken;
              onRrefreshed(data);
              isRefreshing = false;
              refreshSubscribers = [];
              resolve(axiosInstance(originalRequest));
            })
            .catch((err) => {
              logout();
              isRefreshing = false;
              reject(err);
            });
        });
      }

      return new Promise((resolve) => {
        const callIndex = refreshSubscribers.length;
        addRefreshSubscriber(callIndex, (data: TokenModel) => {
          originalRequest.headers["Authorization"] = "Bearer " + data.token;
          originalRequest._retry = true;
          resolve(axiosInstance(originalRequest));
        });
      });
    }

    return Promise.reject(error);
  }
);

export default axiosInstance;
